import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { verifyInvitationToken, acceptInvitationToken, type InvitationDetail } from '../../api/invitation';
import { CheckCircle2, XCircle, Loader2, UserCheck, ShieldCheck, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

export const AcceptInvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState<InvitationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Token undangan tidak valid.');
      setLoading(false);
      return;
    }

    verifyInvitationToken(token)
      .then((data) => {
        setInvitation(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'Undangan tidak ditemukan, sudah kadaluarsa, atau telah digunakan.');
        setLoading(false);
      });
  }, [token]);

  const [password, setPassword] = useState('');

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    try {
      // Jika pengguna adalah Pihak Owner/Klien (bukan tim pelaksana) & belum punya akun
      if (invitation && !invitation.user_exists && !invitation.is_tim_pelaksana) {
        toast.success('Mengarahkan ke halaman pendaftaran Pelanggan/Owner...');
        const fullName = invitation.name || '';
        const parts = fullName.trim().split(' ');
        const firstName = parts[0] || '';
        const lastName = parts.slice(1).join(' ') || '';
        
        setTimeout(() => {
          const backendUrl = import.meta.env.VITE_API_BASE_URL 
            ? import.meta.env.VITE_API_BASE_URL.replace(/\/api\/?$/, '') 
            : (import.meta.env.DEV ? 'http://127.0.0.1:8000' : 'https://indoteknisi.net');
            
          const registerUrl = `${backendUrl}/register?email=${encodeURIComponent(invitation.email)}&first_name=${encodeURIComponent(firstName)}&last_name=${encodeURIComponent(lastName)}&invitation_token=${token}`;
          window.location.href = registerUrl;
        }, 1000);
        return;
      }

      // Validasi password untuk akun Tim Pelaksana baru
      if (invitation && !invitation.user_exists && invitation.is_tim_pelaksana) {
        if (!password || password.length < 8) {
          toast.error('Silakan buat kata sandi minimal 8 karakter untuk akun Anda.');
          setAccepting(false);
          return;
        }
      }

      const content = await acceptInvitationToken(token, password);

      if (content?.auth_token) {
        localStorage.setItem('token', content.auth_token);
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userRole', 'provider');
      }

      toast.success('Selamat! Akun Anda telah aktif dan resmi bergabung di proyek.');
      setTimeout(() => {
        window.location.href = '/project-management';
      }, 1200);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Gagal menerima undangan.';
      toast.error(msg);
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900 flex flex-col items-center justify-center text-white p-4">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
        <p className="text-slate-300 text-lg font-medium">Memverifikasi tautan undangan...</p>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center shadow-xl">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/20">
            <XCircle className="w-10 h-10 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Tautan Tidak Valid</h2>
          <p className="text-slate-400 text-sm mb-6">{error || 'Undangan ini sudah tidak berlaku.'}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 px-4 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition duration-200"
          >
            Kembali ke Beranda
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900 flex flex-col items-center justify-center p-4">
      <div className="max-w-lg w-full bg-slate-800 border border-slate-700 rounded-2xl p-8 shadow-2xl relative overflow-hidden">
        {/* Glow Header Effect */}
        <div className="absolute -top-24 -left-24 w-48 h-48 bg-blue-600/20 rounded-full blur-3xl" />
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-indigo-600/20 rounded-full blur-3xl" />

        <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-blue-500/20">
          <UserCheck className="w-8 h-8 text-blue-400" />
        </div>

        <h1 className="text-2xl font-bold text-white text-center mb-2">Undangan Bergabung Proyek</h1>
        <p className="text-slate-400 text-sm text-center mb-8">
          Anda telah diundang oleh <span className="text-white font-medium">{invitation.inviter_name || 'Sistem'}</span> 
          {invitation.inviter_company && invitation.inviter_company !== '-' ? ` dari ${invitation.inviter_company}` : ''} untuk bergabung ke dalam proyek <span className="text-white font-medium">{invitation.project_name && invitation.project_name !== '-' ? `"${invitation.project_name}"` : 'MANPRO'}</span>.
        </p>

        <div className="bg-slate-900/60 rounded-xl p-5 border border-slate-700/60 mb-6 space-y-3">
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-400">Penerima:</span>
            <span className="font-semibold text-white">{invitation.name || invitation.email}</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-400">Email Contact:</span>
            <span className="font-semibold text-white">{invitation.email}</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-400">Proyek:</span>
            <span className="font-semibold text-white">{invitation.project_name && invitation.project_name !== '-' ? invitation.project_name : 'N/A'}</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-400">Pengundang:</span>
            <span className="font-semibold text-white">{invitation.inviter_name || '-'}</span>
          </div>
          {invitation.inviter_company && invitation.inviter_company !== '-' && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-400">Perusahaan:</span>
              <span className="font-semibold text-white">{invitation.inviter_company}</span>
            </div>
          )}
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-400">Peran yang Ditugaskan:</span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
              <ShieldCheck className="w-3.5 h-3.5 mr-1" />
              {invitation.role?.toUpperCase()}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-400">Status Undangan:</span>
            <span className="inline-flex items-center text-xs font-medium text-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              {invitation.status?.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Input Kata Sandi untuk Pengguna Baru Tim Pelaksana */}
        {!invitation.user_exists && invitation.is_tim_pelaksana && (
          <div className="mb-6 bg-slate-900/80 p-4 rounded-xl border border-blue-500/30">
            <label className="block text-sm font-semibold text-blue-300 mb-1.5">
              Buat Kata Sandi Akun Anda <span className="text-red-400">*</span>
            </label>
            <input
              type="password"
              required
              minLength={8}
              placeholder="Masukkan kata sandi (min. 8 karakter)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-sm transition"
            />
            <p className="mt-1.5 text-xs text-slate-400">
              Kata sandi ini akan digunakan untuk login kembali ke portal Indoteknisi di lain waktu.
            </p>
          </div>
        )}

        <button
          onClick={handleAccept}
          disabled={accepting}
          className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 transition duration-200 disabled:opacity-50"
        >
          {accepting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Memproses Undangan...
            </>
          ) : (
            <>
              Terima Undangan & Masuk Proyek
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};
